import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, Save, ExternalLink, Link2, Calendar as CalIcon, Sparkles, AlertTriangle, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import GermanDateInput from "@/components/GermanDateInput";

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Coach-facing panel: lists every section of a client's Maßnahme and lets the
 * coach confirm the deliverable (Ergebnis erstellt + In TN-Akte hochgeladen +
 * Google-Drive-Link). Read-only preview if user is a client.
 */
export default function SectionDeliverablesPanel({ clientId, readOnly = false }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // section object being edited

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(readOnly ? `/me/deliverables` : `/clients/${clientId}/deliverables`);
      setSections(data.sections || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, readOnly]);

  // group by module_name
  const grouped = sections.reduce((acc, s) => {
    const key = s.module_name || "Weitere Abschnitte";
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-sm text-slate-500 py-6 text-center">Lade Abschnitte…</div>;
  }
  if (!sections.length) {
    return <div className="text-sm text-slate-500 py-6 text-center">Noch keine Abschnitte für diese Maßnahme angelegt.</div>;
  }

  return (
    <div className="border border-slate-200 bg-white mb-6" data-testid="section-deliverables-panel">
      <div className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Abschnitts-Ergebnisse</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
            Fortschritt & Nachweise
          </div>
        </div>
        <div className="text-xs font-mono text-slate-500">
          {sections.filter((s) => s.deliverable?.uploaded).length} / {sections.length} in TN-Akte
        </div>
      </div>

      {Object.entries(grouped).map(([mod, secs]) => (
        <div key={mod} className="border-t border-slate-100 first:border-t-0">
          <div className="px-5 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis">{mod}</div>
          <div className="divide-y divide-slate-100">
            {secs.map((s) => (
              <SectionRow key={s.section_id} section={s} readOnly={readOnly} onEdit={() => setEditing(s)} />
            ))}
          </div>
        </div>
      ))}

      {editing && !readOnly && (
        <DeliverableDialog
          clientId={clientId}
          section={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SectionRow({ section, readOnly, onEdit }) {
  const d = section.deliverable;
  const done = !!d?.result_created_at;
  const uploaded = !!d?.uploaded;
  return (
    <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`section-row-${section.section_id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900">{section.name}</span>
          <TimelineChip status={section.timeline_status} deadline={section.deadline} daysLeft={section.days_left} />
        </div>
        <div className="mt-1 text-[12px] text-slate-600 flex items-center gap-2 flex-wrap">
          <Sparkles size={12} strokeWidth={1.5} className="text-slate-400" />
          <span className="text-slate-500">Erwartetes Ergebnis:</span>
          <span className="font-medium text-slate-800">{d?.title || section.suggested_title || "Ergebnis"}</span>
        </div>
        <div className="mt-2 flex items-center gap-4 text-[12px] flex-wrap">
          <StatusChip done={done} label="Ergebnis erstellt" date={d?.result_created_at} />
          <StatusChip done={uploaded} label="In TN-Akte hochgeladen" date={d?.uploaded_at} />
          {d?.drive_link ? (
            <a href={d.drive_link} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[12px] text-[#009F9C] hover:underline"
               data-testid={`drive-link-${section.section_id}`}>
              <Link2 size={12} strokeWidth={1.5} /> Google Drive öffnen <ExternalLink size={10} />
            </a>
          ) : null}
        </div>
        {d?.notes ? (
          <div className="mt-2 text-[12px] text-slate-500 italic">&bdquo;{d.notes}&ldquo;</div>
        ) : null}
      </div>
      <div>
        {!readOnly && (
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
            data-testid={`edit-deliverable-${section.section_id}`}
          >
            {d ? "Bearbeiten" : "Ergebnis bestätigen"}
          </button>
        )}
      </div>
    </div>
  );
}

function TimelineChip({ status, deadline, daysLeft }) {
  if (!status || status === "n/a") return null;
  const meta = {
    on_time:  { Icon: Clock,          cls: "border-emerald-300 bg-emerald-50 text-emerald-800", label: "On Time" },
    due_soon: { Icon: AlertTriangle,  cls: "border-amber-300 bg-amber-50 text-amber-800",       label: "Bald fällig" },
    overdue:  { Icon: AlertTriangle,  cls: "border-red-300 bg-red-50 text-red-800",             label: "Verzögert" },
    done:     { Icon: CheckCircle2,   cls: "border-emerald-400 bg-emerald-100 text-emerald-900", label: "Erledigt" },
  }[status];
  if (!meta) return null;
  const dl = deadline ? new Date(deadline + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
  const daysTxt = status === "overdue"
    ? `${Math.abs(daysLeft)}T zu spät`
    : (typeof daysLeft === "number" ? `${daysLeft}T` : "");
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border font-medium ${meta.cls}`}
          title={`Deadline: ${dl}`}>
      <meta.Icon size={10} strokeWidth={2} />
      <span>{meta.label}</span>
      {dl && <span className="opacity-75">· {dl}</span>}
      {daysTxt && <span className="opacity-75">· {daysTxt}</span>}
    </span>
  );
}

function StatusChip({ done, label, date }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border ${done ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
      {done ? <CheckCircle2 size={11} strokeWidth={2} /> : <Circle size={11} strokeWidth={1.5} />}
      <span>{label}</span>
      {done && date ? <span className="opacity-70">· {formatDeShort(date)}</span> : null}
    </span>
  );
}

function DeliverableDialog({ clientId, section, onClose, onSaved }) {
  const d = section.deliverable || {};
  const [form, setForm] = useState({
    title: d.title || section.suggested_title || "Ergebnis",
    drive_link: d.drive_link || "",
    result_created_at: d.result_created_at || "",
    uploaded_at: d.uploaded_at || "",
    uploaded: !!d.uploaded,
    notes: d.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/deliverables`, {
        section_id: section.section_id,
        title: form.title || null,
        drive_link: form.drive_link || null,
        result_created_at: form.result_created_at || null,
        uploaded_at: form.uploaded_at || null,
        uploaded: form.uploaded,
        notes: form.notes || null,
      });
      toast.success("Ergebnis gespeichert");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            Ergebnis bestätigen
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {section.module_name ? `${section.module_name} · ` : ""}{section.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Bezeichnung des Ergebnisses">
            <input value={form.title} onChange={(e) => set("title", e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="z. B. Berufsprofil & Karriereeinschätzung (PDF)"
              data-testid="deliverable-title" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ergebnis erstellt am">
              <GermanDateInput value={form.result_created_at || ""} onChange={(e) => set("result_created_at", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="deliverable-result-date" />
              <button type="button" onClick={() => set("result_created_at", todayIso())}
                className="mt-1 text-[11px] text-[#009F9C] hover:underline">Heute setzen</button>
            </Field>
            <Field label="In TN-Akte hochgeladen am">
              <GermanDateInput value={form.uploaded_at || ""} onChange={(e) => set("uploaded_at", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="deliverable-upload-date" />
              <button type="button" onClick={() => set("uploaded_at", todayIso())}
                className="mt-1 text-[11px] text-[#009F9C] hover:underline">Heute setzen</button>
            </Field>
          </div>

          <Field label="Google-Drive-Link (TN-Akte)">
            <input value={form.drive_link} onChange={(e) => set("drive_link", e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900 font-mono"
              placeholder="https://drive.google.com/…"
              data-testid="deliverable-drive-link" />
          </Field>

          <label className="flex items-center gap-2 text-sm" data-testid="deliverable-uploaded-toggle">
            <input type="checkbox" checked={form.uploaded} onChange={(e) => set("uploaded", e.target.checked)}
              className="accent-[#00CCC9] w-4 h-4" />
            <span>Ich bestätige: Ergebnis wurde in der TN-Akte (Google Drive) abgelegt</span>
          </label>

          <Field label="Notizen (optional)">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="Interne Anmerkungen zum Ergebnis…"
              data-testid="deliverable-notes" />
          </Field>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
            data-testid="save-deliverable-btn">
            <Save size={14} strokeWidth={1.5} /> {saving ? "Speichern…" : "Speichern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
